import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { GeneratedDoorPlacement, DoorUnit, DoorDescriptor } from "building-types/doors/doorTypes";
import { type OpeningSide } from "building-types/openings/openingTypes";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { closestParamNormalizedLine2, closestParamLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { polyLine2EdgeTangent, polyLine2Length, polyLine2EdgeEval, polyLine2Start, polyLine2End } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Dot, vec2Result } from "geom/vec2";
import { makeOpeningSide } from "../openings/openingProfile";
import { rectilinearOpeningProfile } from "../openings/openingUtils";
import { wallBaseLine } from "../walls/wallBaseline";
import { doorHasCasing } from "./doorQuery";

export const DOOR_JAMB_THICKNESS = 0.5;

export function isDoorUnitCurved(doorUnit: DoorUnit): boolean {
  return !!doorUnit.arcCurve && doorUnit.arcCurve.length > 0;
}

export function getFinalDoorPosition(
  door: GeneratedDoorPlacement,
  frontEdge: PolyLine2,
  edgeLength: number,
  wallMassSystem: WallMassSystem,
): OpeningSide | null {
  switch (door.type) {
    case "DOOR": {
      let centerParam = door.param;
      let centerStripParam = centerParam;
      let stripLength = edgeLength;

      const wallIndex = wallMassSystem.wallIndex;
      const wall = wallMassSystem.walls[wallMassSystem.halfEdgeToWall[door.halfEdgeIndex]];

      if (door.wallStripDomain) {
        const wallsInRoom = wallIndex.wallsInRoom(door.frontRoom);
        const roomWallIndex = wallsInRoom.indexOf(wall);
        if (!(roomWallIndex >= 0)) {
          console.assert(false, "wall not found in room parent. this error is caused by a malformed planar graph that could arise from an illegal room configuration.");
        }
        const wallBL = wallBaseLine(door.frontRoom, wallsInRoom, roomWallIndex);
        const [facedirx, facediry] = polyLine2EdgeTangent(frontEdge, 0);
        const wallStrip = wallIndex.parentWallStrip(door.frontRoom, wall);
        if (!wallStrip) {
          console.warn("wallstrip null from wallIndex.parentWallStrip() in getFinalDoorPosition method");
          return null;
        }
        const wallStripBL = wallStrip.baselineMaxOffset(true);
        const [wallstx, wallsty] = polyLine2EdgeTangent(wallStripBL, 0);
        stripLength = polyLine2Length(wallStripBL);

        // evaluate point on wall strip base line

        let wallStripParam = door.param;

        if (wallStripParam < 0) {
          if (door.paramNormalized) {
            wallStripParam = wallStripParam + 1.0;
          } else {
            wallStripParam = wallStripParam + polyLine2Length(wallStripBL);
          }
        }

        const [x, y] = polyLine2EdgeEval(wallStripBL, 0, wallStripParam, door.paramNormalized);
        assertIsNotNullOrUndefined(wallBL);
        centerParam = door.paramNormalized
          ? closestParamNormalizedLine2(wallBL[0], wallBL[1], wallBL[2], wallBL[3], x, y, false)
          : closestParamLine2(wallBL[0], wallBL[1], wallBL[2], wallBL[3], x, y, false);

        centerStripParam = door.paramNormalized
          ? closestParamNormalizedLine2(wallStripBL[0], wallStripBL[1], wallStripBL[2], wallStripBL[3], x, y, false)
          : closestParamLine2(wallStripBL[0], wallStripBL[1], wallStripBL[2], wallStripBL[3], x, y, false);

        if (door.paramNormalized && (centerStripParam > 1.0 || centerStripParam < 0.0)) {
          return null;
        } else if (centerStripParam > stripLength || centerStripParam < 0) {
          return null;
        }

        // reverse the direction of the param if it doesn't align with the room bounday
        if (vec2Dot(wallstx, wallsty, facedirx, facediry) < 0) {
          centerStripParam = -centerStripParam;
          centerParam = -centerParam;
        }
      }

      if (door.paramNormalized) {
        centerParam = edgeLength * centerParam;
        centerStripParam = stripLength * centerStripParam;
      }

      if (centerParam < 0) {
        centerParam = edgeLength + centerParam;
      }

      if (centerStripParam < 0) {
        centerStripParam = stripLength + centerStripParam;
      }

      let width = door.descriptor.width;
      switch (door.descriptor.id) {
        case BuiltinDoorId.OPEN_FULL: {
          width = levelInchesNew(edgeLength - 6);
          break;
        }
        case BuiltinDoorId.OPEN_ARC_FULL: {
          width = levelInchesNew(edgeLength - 6);
          break;
        }
        case BuiltinDoorId.CL_BYPASS: {
          width = levelInchesNew(edgeLength - 6);
          break;
        }
      }
      const height = door.descriptor.height;
      const depth = getDoorOpeningHeight(door.descriptor);

      const halfWidth = width / 2;
      const startParam = centerParam - halfWidth;
      const endParam = centerParam + halfWidth;
      const startStripParam = centerStripParam - halfWidth;
      const endStripParam = centerStripParam + halfWidth;

      if (
        !door.useStripLimits &&
        (endParam > edgeLength + DIST_EPSILON || endParam < 0 || startParam > edgeLength || startParam < 0)
      ) {
        return null;
      }

      if (
        door.useStripLimits &&
        (endStripParam > stripLength || endStripParam < 0 || startStripParam > stripLength || startStripParam < 0)
      ) {
        return null;
      }

      const { verticalOffset = 0 } = door;
      const wallBaseOffset = wall.wallType === WallType.SOLID_MASS ? wall.path.baseOffset : 0;

      const top = levelInchesNew(height - wallBaseOffset + verticalOffset);
      const bottom = levelInchesNew(top - depth);

      return makeOpeningSide({
        ...rectilinearOpeningProfile({
          start: startParam,
          end: endParam,
          top,
          bottom,
          streetOrientation: door.streetOrientation,
          exteriorCasingSettings: door.exteriorCasing as ExteriorDoorCasingSettings,
        }),
        hasCasing: doorHasCasing(door, door.exteriorCasing as ExteriorDoorCasingSettings),
        startParam,
        endParam,
      });
    }
    case "DOOR_V2": {
      const wall = wallMassSystem.walls[wallMassSystem.halfEdgeToWall[door.halfEdgeIndex]];

      const { x, y } = door;
      if (x === undefined || y === undefined) {
        return null;
      }
      const [sx, sy] = polyLine2Start(frontEdge);
      const [ex, ey] = polyLine2End(frontEdge);

      const t = closestParamLine2(sx, sy, ex, ey, x, y, false);
      const centerParam = t;

      let width = door.descriptor.width;
      switch (door.descriptor.id) {
        case BuiltinDoorId.OPEN_FULL:
        case BuiltinDoorId.OPEN_ARC_FULL:
        case BuiltinDoorId.CL_BYPASS: {
          width = levelInchesNew(edgeLength - 6);
          break;
        }
      }
      const height = door.descriptor.height;
      const depth = getDoorOpeningHeight(door.descriptor);

      const halfWidth = width / 2;
      const startParam = centerParam - halfWidth;
      const endParam = centerParam + halfWidth;

      const { verticalOffset = 0 } = door;
      const wallBaseOffset = wall.wallType === WallType.SOLID_MASS ? wall.path.baseOffset : 0;
      const top = levelInchesNew(height - wallBaseOffset + verticalOffset);
      const bottom = levelInchesNew(top - depth);

      return makeOpeningSide({
        hasCasing: doorHasCasing(door, door.exteriorCasing as ExteriorDoorCasingSettings),
        ...rectilinearOpeningProfile({
          start: startParam,
          end: endParam,
          top,
          bottom,
          streetOrientation: door.streetOrientation,
          exteriorCasingSettings: door.exteriorCasing as ExteriorDoorCasingSettings,
        }),
        startParam,
        endParam,
      });
    }
  }
}

export function getArcCenter(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): Vec2 {
  x1 -= x0;
  y1 -= y0;
  x2 -= x0;
  y2 -= y0;

  const z1 = x1 * x1 + y1 * y1;
  const z2 = x2 * x2 + y2 * y2;
  const d = 2 * (x1 * y2 - x2 * y1);

  vec2Result[0] = (z1 * y2 - z2 * y1) / d + x0;
  vec2Result[1] = (x1 * z2 - x2 * z1) / d + y0;

  return vec2Result;
}

// TODO - fix this - move to doorOperationTypes
export function isGarageDoorOperationType(door: Pick<GeneratedDoorPlacement, "descriptor">) {
  return (
    door?.descriptor?.operationType === DoorOperationType.GARAGE_DOUBLE ||
    door?.descriptor?.operationType === DoorOperationType.GARAGE_SINGLE
  );
}

// TODO - fix this - move to doorOperationTypes
export function isPocketDoorOperationType(door: GeneratedDoorPlacement) {
  return door.descriptor.operationType === DoorOperationType.POCKET;
}

export function getDefaultDoorRoughOpeningAdjustment(door: GeneratedDoorPlacement): number {
  return door.descriptor.roughOpeningAdjustment ?? 0;
}

export function getDoorRoughOpeningAdjustment(door: GeneratedDoorPlacement, doorSettings: DoorSettings): number {
  const { garageDoorRoughOpeningSize } = doorSettings;
  const defaultDoorRoughOpeningAdjustment = getDefaultDoorRoughOpeningAdjustment(door);

  if (isGarageDoorOperationType(door)) {
    return garageDoorRoughOpeningSize ?? defaultDoorRoughOpeningAdjustment;
  } else {
    return defaultDoorRoughOpeningAdjustment;
  }
}

export function getDoorOpeningHeight(descriptor: DoorDescriptor): LevelInches {
  return levelInchesNew(descriptor.height - descriptor.zOffset);
}
